import { useMemo } from 'react'

import {
  digObject, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { PriorityCampaignModel, PriorityCampaignRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'
import type { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['priorityCampaigns']

type HandleSortParams = {
  priorityCampaign: PriorityCampaignModel,
  sortKey?: string,
  users: EntitiesState['users'],
}

const handleSort = (params: HandleSortParams): string | number => {
  const {
    priorityCampaign,
    sortKey,
    users,
  } = params

  if (sortKey === 'user_full_name' && priorityCampaign.owner_type === 'User'){
    // @ts-expect-error TS2322
    return users[priorityCampaign.owner_id]?.full_name
  }

  return priorityCampaign.id
}

type PriorityCampaignFilters = {
  campaign_id?: number,
  hidden?: boolean,
  owner_id?: number,
  owner_type?: string,
}

type UsePriorityCampaignsOptions = {
  filters?: PriorityCampaignFilters,
  performHttpRequests?: boolean,
  requestOptions?: PriorityCampaignRequestOptions,
  sortKey?: string,
}

function usePriorityCampaigns(options: UsePriorityCampaignsOptions) {
  const { filters = {}, requestOptions, sortKey = 'user_full_name' } = options
  const {
    campaign_id: filterCampaignId,
    hidden: filterHidden,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
  } = filters

  const {
    updatedEntities: {
      priorityCampaigns: priorityCampaignsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaigns, priorityCampaigns, users } = useSelector(reduxState => reduxState.entities)

  const filteredPriorityCampaigns = useMemo(() => {
    const filtered = Object.values(priorityCampaigns).filter((priorityCampaign) => {
      const { campaign_id, owner_id, owner_type } = priorityCampaign

      const campaign = campaign_id ? campaigns[campaign_id] || {} : {}
      const campaignHidden = digObject(campaign, 'hidden', false)

      const matchFilterCampaign = matchFilterNumber(campaign_id, filterCampaignId)

      const matchOwnerType = matchFilterString(owner_type, filterOwnerType)

      const matchHidden = filterHidden ? campaignHidden.toString() === filterHidden : true

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)

      return matchHidden
      && matchFilterCampaign
      && matchOwnerId
      && matchOwnerType
    })

    return sortArrayBy(
      filtered,
      'asc',
      priorityCampaign => handleSort({ priorityCampaign, sortKey, users }),
    )
  }, [priorityCampaignsUpdatedAt, JSON.stringify(filters)])

  const filteredPriorityCampaignsCount = filteredPriorityCampaigns.length
  const hasFilteredPriorityCampaigns = !!filteredPriorityCampaignsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPriorityCampaignsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPriorityCampaigns } = useReduxAction(
    'priorityCampaigns',
    'loadPriorityCampaigns',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPriorityCampaigns,
    filteredPriorityCampaignsCount,
    hasFilteredPriorityCampaigns,
    loading: loadingPriorityCampaigns,
  }
}

export default usePriorityCampaigns
